// Numeric codes ported from server/OriginalServerFiles/World Server/datatypes.h:64-170.
// Two overlapping numbering ranges exist in the original source (an older
// set and a newer "alternate" set) and both are kept here because it's
// not possible to tell statically which range a given item's stat1/stat2
// or gem bonus actually uses -- see docs/superpowers/specs/2026-09-14-item-tooltip-design.md.
export enum StatType {
  NONE = 0,
  STR = 10,
  DEX = 11,
  INT = 12,
  CON = 13,
  CHA = 14,
  SEN = 15,
  HP = 16,
  MP = 17,
  ATTACK = 18,
  DEFENSE = 19,
  ACCURACY = 20,
  MAGIC_RESIST = 21,
  DODGE = 22,
  DASH = 23,
  HASTE = 24,
  INVENTORY_CAPACITY = 25,
  CRITICAL = 26,
  HP_REC_RATE = 27,
  MP_REC_RATE = 28,
  MAX_HP = 38,
  MAX_MP = 39,
  DEFENSE_ALT = 53,
  MAX_HP_ALT = 54,
  MAX_MP_ALT = 55,
  HP_REC_AMOUNT = 56,
  MP_REC_RATE_ALT = 57,
  BAGPACK_CAPACITY = 58,
  MP_COST_RED = 61,
  SUMMON_GAUGE = 62,
  MAGIC_RESIST_ALT = 98,
  ACCURACY_ALT = 99,
  CRITICAL_ALT = 100,
  DODGE_ALT = 101,
}

export interface DecodedStat {
  type: StatType;
  label: string;
  value: number;
  isPercent: boolean;
}

const STAT_LABELS: Partial<Record<StatType, string>> = {
  [StatType.STR]: "Strength",
  [StatType.DEX]: "Dexterity",
  [StatType.INT]: "Intelligence",
  [StatType.CON]: "Concentration",
  [StatType.CHA]: "Charm",
  [StatType.SEN]: "Sensibility",
  [StatType.HP]: "HP",
  [StatType.MP]: "MP",
  [StatType.ATTACK]: "Attack Power",
  [StatType.DEFENSE]: "Defense",
  [StatType.ACCURACY]: "Accuracy",
  [StatType.MAGIC_RESIST]: "Magic Resistance",
  [StatType.DODGE]: "Dodge",
  [StatType.DASH]: "Move Speed",
  [StatType.HASTE]: "Attack Speed",
  [StatType.INVENTORY_CAPACITY]: "Inventory Capacity",
  [StatType.CRITICAL]: "Critical",
  [StatType.HP_REC_RATE]: "HP Recovery",
  [StatType.MP_REC_RATE]: "MP Recovery",
  [StatType.MAX_HP]: "Max HP",
  [StatType.MAX_MP]: "Max MP",
  [StatType.DEFENSE_ALT]: "Defense",
  [StatType.MAX_HP_ALT]: "Max HP",
  [StatType.MAX_MP_ALT]: "Max MP",
  [StatType.HP_REC_AMOUNT]: "HP Recovery",
  [StatType.MP_REC_RATE_ALT]: "MP Recovery",
  [StatType.BAGPACK_CAPACITY]: "Inventory Capacity",
  [StatType.MP_COST_RED]: "MP Consumption",
  [StatType.SUMMON_GAUGE]: "Summon Gauge",
  [StatType.MAGIC_RESIST_ALT]: "Magic Resistance",
  [StatType.ACCURACY_ALT]: "Accuracy",
  [StatType.CRITICAL_ALT]: "Critical",
  [StatType.DODGE_ALT]: "Dodge",
};

const PERCENT_STATS = new Set<StatType>([
  StatType.HASTE,
  StatType.CRITICAL,
  StatType.CRITICAL_ALT,
  StatType.HP_REC_RATE,
  StatType.MP_REC_RATE,
  StatType.MP_REC_RATE_ALT,
  StatType.MP_COST_RED,
  StatType.DASH,
]);

export function decodeStatOption(type: number, value: number): DecodedStat | null {
  const label = STAT_LABELS[type as StatType];
  if (!type || !label) {
    return null;
  }
  return {
    type: type as StatType,
    label,
    value,
    isPercent: PERCENT_STATS.has(type as StatType),
  };
}

const ALL_STATS_GROUP: StatType[] = [
  StatType.STR,
  StatType.DEX,
  StatType.INT,
  StatType.CON,
  StatType.CHA,
  StatType.SEN,
];

export function collapseAllStats(stats: DecodedStat[]): DecodedStat[] {
  const byType = new Map(stats.map((s) => [s.type, s]));
  const groupStats = ALL_STATS_GROUP.map((t) => byType.get(t)).filter(
    (s): s is DecodedStat => s !== undefined
  );
  const allPresent = groupStats.length === ALL_STATS_GROUP.length;
  const allEqual = allPresent && groupStats.every((s) => s.value === groupStats[0].value);
  if (!allPresent || !allEqual) {
    return stats;
  }
  const rest = stats.filter((s) => !ALL_STATS_GROUP.includes(s.type));
  return [
    { type: StatType.NONE, label: "All Stats", value: groupStats[0].value, isPercent: false },
    ...rest,
  ];
}

// Requirement codes from server/OriginalServerFiles/World Server/playerfunctions.cpp:2356-2432.
export enum RequirementType {
  NONE = 0,
  STRENGTH = 10,
  DEXTERITY = 11,
  INTELLIGENCE = 12,
  CONCENTRATION = 13,
  CHARM = 14,
  SENSIBILITY = 15,
  LEVEL = 31,
}

export interface PlayerStatsForRequirement {
  level: number;
  str: number;
  dex: number;
  int: number;
  con: number;
  cha: number;
  sen: number;
}

const REQUIREMENT_LABELS: Partial<Record<RequirementType, string>> = {
  [RequirementType.LEVEL]: "Level",
  [RequirementType.STRENGTH]: "Strength",
  [RequirementType.DEXTERITY]: "Dexterity",
  [RequirementType.INTELLIGENCE]: "Intelligence",
  [RequirementType.CONCENTRATION]: "Concentration",
  [RequirementType.CHARM]: "Charm",
  [RequirementType.SENSIBILITY]: "Sensibility",
};

const REQUIREMENT_STAT_KEYS: Partial<Record<RequirementType, keyof PlayerStatsForRequirement>> = {
  [RequirementType.LEVEL]: "level",
  [RequirementType.STRENGTH]: "str",
  [RequirementType.DEXTERITY]: "dex",
  [RequirementType.INTELLIGENCE]: "int",
  [RequirementType.CONCENTRATION]: "con",
  [RequirementType.CHARM]: "cha",
  [RequirementType.SENSIBILITY]: "sen",
};

export interface DecodedRequirement {
  label: string;
  required: number;
  current: number;
  met: boolean;
}

export function decodeRequirement(
  reqType: number,
  reqValue: number,
  player: PlayerStatsForRequirement
): DecodedRequirement | null {
  const label = REQUIREMENT_LABELS[reqType as RequirementType];
  const key = REQUIREMENT_STAT_KEYS[reqType as RequirementType];
  if (!reqType || !label || !key) {
    return null;
  }
  const current = player[key];
  return { label, required: reqValue, current, met: current >= reqValue };
}
